import type { MantineColorsTuple } from "@mantine/core"
import { createTheme, rem } from "@mantine/core"

// ─────────────────────────────────────────────────────────────────────
// Pitch theme — institutional fundraising aesthetic.
// Used by apps/invest (and apps/investor pending migration).
//
// Palette per Gregg McConnell v2 site update (May 2026):
//   • navy lifted from #0A1929 → #1F4E79 (medium navy for strong fills)
//   • new "pageDark" #001B3A for full-bleed dark surfaces (hero/footer)
//   • negative semantic moved from crayon-red to caution-amber #C55A11
//   • positive shifted to olive #548235
//   • inflection unified with negative (same hex, different semantic)
//   • alpha-vc neutral grey (was crimson); alpha-sourcing primary blue
//     (was teal); alpha-fee darker navy
// ─────────────────────────────────────────────────────────────────────

// Navy primary — text + chrome around #1F4E79
const pitchNavy: MantineColorsTuple = [
  "#EAEFF5",
  "#D2DDEA",
  "#A6BCD5",
  "#7A9BC0",
  "#4E7AAB",
  "#356395",
  "#1F4E79",
  "#19405F",
  "#13314A",
  "#0C2030",
]

// Accent blue — interactive primary (#2E75B6)
const pitchAccent: MantineColorsTuple = [
  "#D6E4F0",
  "#C0D5E8",
  "#A0BFDA",
  "#7BA1CC",
  "#5288BD",
  "#3978B0",
  "#2E75B6",
  "#246199",
  "#1B4D7C",
  "#13395E",
]

// Negative — caution-amber (#C55A11). v2 semantic = caution, not crayon-red.
const pitchNegative: MantineColorsTuple = [
  "#FCE4CC",
  "#F8C99A",
  "#F4AE68",
  "#F09336",
  "#DC7C18",
  "#CE6914",
  "#C55A11",
  "#9F480E",
  "#793608",
  "#532506",
]

// Positive — olive (#548235)
const pitchPositive: MantineColorsTuple = [
  "#E2EFDA",
  "#C7DFB5",
  "#ACCF90",
  "#91BF6B",
  "#76AF46",
  "#65963B",
  "#548235",
  "#446B2B",
  "#335520",
  "#223E16",
]

// Inflection — same caution-amber as negative per v2 (unified)
const pitchInflection: MantineColorsTuple = pitchNegative

// ─────────────────────────────────────────────────────────────────────
// Legacy color aliases — kept for in-flight migration of apps that
// still reference Mantine color="civala*" props (portal, nexus). They
// map the old purple/data palette onto pitch equivalents so existing
// page code adopts the pitch L&F without a page-by-page rewrite.
//
// TODO: Replace civala* references with explicit pitch* names and
// remove these aliases.
// ─────────────────────────────────────────────────────────────────────
const civalaViolet: MantineColorsTuple = pitchNavy
const civalaBlue: MantineColorsTuple = pitchAccent
const civalaGreen: MantineColorsTuple = pitchPositive
const civalaOrange: MantineColorsTuple = pitchNegative
// Muted gold — used for premium/highlight accents in legacy pages.
const civalaGold: MantineColorsTuple = [
  "#FAF1D7",
  "#F2E0A8",
  "#E9CE79",
  "#E0BC4A",
  "#D6A91F",
  "#C29A0E",
  "#BF8F00",
  "#9A7300",
  "#755800",
  "#503C00",
]
// Slate gray — was the dataTheme neutral. Maps to a cool gray that reads
// well alongside pitchNavy.
const slate: MantineColorsTuple = [
  "#F8FAFC",
  "#F1F5F9",
  "#E2E8F0",
  "#CBD5E1",
  "#94A3B8",
  "#64748B",
  "#475569",
  "#334155",
  "#1E293B",
  "#0F172A",
]
// Navy (dataTheme alias) — same tuple as pitchNavy.
const navy: MantineColorsTuple = pitchNavy

export const pitchTheme = createTheme({
  primaryColor: "pitchAccent",
  colors: {
    pitchNavy,
    pitchAccent,
    pitchNegative,
    pitchPositive,
    pitchInflection,
    // Legacy aliases — see TODO above.
    civalaViolet,
    civalaBlue,
    civalaGreen,
    civalaOrange,
    civalaGold,
    navy,
    slate,
  },
  white: "#FFFFFF",
  black: "#001B3A",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  headings: {
    fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif",
    // 500 is the canonical Civala heading weight (matches the mockup screens
    // and PageHeader). Set here so <Title> no longer needs an inline `fw`.
    fontWeight: "500",
  },
  defaultRadius: "xs",
  components: {
    // Cards/surfaces are sharp-edged in the pitch L&F. Set as a Paper default
    // so panels don't each need `radius={0}`. Other components (Badge, Button,
    // Input) keep the `xs` defaultRadius.
    Paper: {
      defaultProps: {
        radius: 0,
      },
    },
  },
  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
  },
  shadows: {
    xs: "0 1px 2px rgba(31, 78, 121, 0.04)",
    sm: "0 1px 3px rgba(31, 78, 121, 0.06)",
    md: "0 4px 6px rgba(31, 78, 121, 0.06), 0 2px 4px rgba(31, 78, 121, 0.04)",
    lg: "0 10px 15px rgba(31, 78, 121, 0.08), 0 4px 6px rgba(31, 78, 121, 0.04)",
    xl: "0 20px 25px rgba(31, 78, 121, 0.10), 0 10px 10px rgba(31, 78, 121, 0.04)",
  },
  other: {
    // Surface tokens
    surface: "#FFFFFF",
    surfaceAlt: "#FAFBFC",
    border: "#E0E0E0",
    borderStrong: "#C5CDD5",

    // Page-dark — for full-bleed hero/footer surfaces
    pageDark: "#001B3A",

    // Text hierarchy
    textPrimary: "#1F2937",
    textSecondary: "#4B5563",
    textTertiary: "#6B7280",

    // Tinted backgrounds for semantic states
    accentBlueLight: "#D6E4F0",
    negativeBg: "#FCE4CC",
    positiveBg: "#E2EFDA",
    inflectionBg: "#FCE4CC",

    // Six Alphas color map — v2 palette
    alphaColors: {
      vc: "#8C8C8C", // neutral grey — VC baseline
      fee: "#1F4E79", // navy
      sourcing: "#2E75B6", // primary blue
      operating: "#548235", // olive
      structural: "#5B9BD5", // light blue
      tax: "#BF8F00", // muted gold
      exit: "#C55A11", // warm amber
    },
  },
})
